import { useState } from "react";
import { supabase } from "../integrations/supabase/client";
import { toast } from "./use-toast";

interface RefreshOptions {
  outletId: string;
  outletName: string;
  isDataForSEO: boolean;
  onProgress?: (progress: number) => void;
  onSuccess?: () => void;
}

export const useOutletRefresh = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshOutlet = async ({ 
    outletId, 
    outletName, 
    isDataForSEO, 
    onProgress,
    onSuccess 
  }: RefreshOptions) => {
    setIsRefreshing(true);
    onProgress?.(0);

    let jobId: string | null = null;

    try {
      // Re-fetch outlet from database to get latest configuration
      console.log("🔄 Re-fetching outlet from database to get latest config...");
      const { data: freshOutlet, error: fetchError } = await supabase
        .from("outlets")
        .select("*")
        .eq("id", outletId)
        .single();

      if (fetchError || !freshOutlet) {
        throw new Error("Failed to fetch latest outlet configuration");
      }

      console.log("🔄 Starting refresh for outlet:", {
        id: freshOutlet.id,
        name: freshOutlet.outlet_name,
        fetchLimit: freshOutlet.fetch_limit,
        isDataForSEO
      });

      // Get user for job records
      const { data: { user } } = await supabase.auth.getUser();
      
      // Create job records for each platform
      // DataForSEO handles: Google Maps, TripAdvisor
      // APIFY handles: Facebook, Instagram, TikTok
      const jobsToCreate = [];
      
      if (isDataForSEO) {
        // DataForSEO outlet - only Google and TripAdvisor
        if (freshOutlet.google_maps_url || freshOutlet.place_id) {
          jobsToCreate.push({
            outlet_id: freshOutlet.id,
            user_id: user?.id,
            platform: "google_maps",
            status: "in_progress",
            source: "dataforseo",
            triggered_by: "manual",
            started_at: new Date().toISOString(),
            message: "Fetching reviews from Google Maps..."
          });
        }
        
        if (freshOutlet.tripadvisor_url) {
          jobsToCreate.push({
            outlet_id: freshOutlet.id,
            user_id: user?.id,
            platform: "tripadvisor",
            status: "in_progress",
            source: "dataforseo",
            triggered_by: "manual",
            started_at: new Date().toISOString(),
            message: "Fetching reviews from TripAdvisor..."
          });
        }
      }
      
      // Add Facebook if available (always APIFY)
      if (freshOutlet.facebook_url) {
        jobsToCreate.push({
          outlet_id: freshOutlet.id,
          user_id: user?.id,
          platform: "facebook",
          status: "in_progress",
          source: "apify",
          triggered_by: "manual",
          started_at: new Date().toISOString(),
          message: "Fetching reviews from Facebook..."
        });
      }
      
      // Add Instagram if available (APIFY)
      if (freshOutlet.instagram_username) {
        jobsToCreate.push({
          outlet_id: freshOutlet.id,
          user_id: user?.id,
          platform: "instagram",
          status: "in_progress",
          source: "apify",
          triggered_by: "manual",
          started_at: new Date().toISOString(),
          message: "Fetching reviews from Instagram..."
        });
      }
      
      // Add TikTok if available (APIFY)
      if (freshOutlet.tiktok_username) {
        jobsToCreate.push({
          outlet_id: freshOutlet.id,
          user_id: user?.id,
          platform: "tiktok",
          status: "in_progress",
          source: "apify",
          triggered_by: "manual",
          started_at: new Date().toISOString(),
          message: "Fetching reviews from TikTok..."
        });
      }
      
      // For non-DataForSEO outlets, add Google Maps if available (APIFY)
      if (!isDataForSEO && (freshOutlet.google_maps_url || freshOutlet.place_id)) {
        jobsToCreate.push({
          outlet_id: freshOutlet.id,
          user_id: user?.id,
          platform: "google_maps",
          status: "in_progress",
          source: "apify",
          triggered_by: "manual",
          started_at: new Date().toISOString(),
          message: "Fetching reviews from Google Maps..."
        });
      }
      
      // For non-DataForSEO outlets, add TripAdvisor if available (APIFY)
      if (!isDataForSEO && freshOutlet.tripadvisor_url) {
        jobsToCreate.push({
          outlet_id: freshOutlet.id,
          user_id: user?.id,
          platform: "tripadvisor",
          status: "in_progress",
          source: "apify",
          triggered_by: "manual",
          started_at: new Date().toISOString(),
          message: "Fetching reviews from TripAdvisor..."
        });
      }
      
      // Insert all job records
      if (jobsToCreate.length > 0) {
        const { data: jobData, error: jobError } = await supabase
          .from("scraping_jobs")
          .insert(jobsToCreate)
          .select();
        
        if (!jobError && jobData && jobData.length > 0) {
          jobId = jobData[0].id;
        }
      }

      onProgress?.(30);

      let data, error;

      if (isDataForSEO) {
        // Use DataForSEO edge function
        const body: any = {
          location_code: freshOutlet.location_code || 2702,
          depth: freshOutlet.fetch_limit || 10,
        };

        if (freshOutlet.data_fetch_mode === "place_id") {
          body.place_id = freshOutlet.place_id;
        } else {
          body.outlet_name = freshOutlet.outlet_name;
          body.branch = freshOutlet.branch;
        }

        // Call dataforseo-fetch-reviews (no timeout needed - let it complete naturally)
        const result = await supabase.functions.invoke("dataforseo-fetch-reviews", {
          body,
        });

        data = result.data;
        error = result.error;

        if (error) throw error;

        if (data?.ok && data?.full_result?.items?.length > 0) {
          onProgress?.(60);

          // Insert reviews into database
          const reviewsToInsert = data.full_result.items.map((review: any) => {
            let postedAt: string;
            try {
              const timestamp = review.timestamp;
              if (timestamp) {
                const cleanTimestamp = timestamp.replace(' +00:00', 'Z').replace(' ', 'T');
                const parsedDate = new Date(cleanTimestamp);
                postedAt = !isNaN(parsedDate.getTime()) 
                  ? parsedDate.toISOString() 
                  : new Date().toISOString();
              } else {
                postedAt = new Date().toISOString();
              }
            } catch (e) {
              console.error("Failed to parse timestamp:", review.timestamp, e);
              postedAt = new Date().toISOString();
            }

            return {
              outlet_id: freshOutlet.id,
              author_name: review.profile_name || "Anonymous",
              comment_text: review.review_text || review.original_review_text || "",
              rating: review.rating?.value || 0,
              platform: "google_maps",
              posted_at: postedAt,
              platform_review_id: review.review_id || null,
            };
          });

          const { error: reviewsError } = await supabase
            .from("reviews")
            .upsert(reviewsToInsert, {
              onConflict: 'platform,platform_review_id'
            });

          if (reviewsError) {
            console.error("⚠️ Error inserting reviews:", reviewsError);
            throw new Error(`Failed to store reviews: ${reviewsError.message}`);
          }

          onProgress?.(100);

          // Update job record with success
          if (jobId) {
            await supabase
              .from("scraping_jobs")
              .update({
                status: "completed",
                finished_at: new Date().toISOString(),
                reviews_fetched: reviewsToInsert.length,
              })
              .eq("id", jobId);
          }

          toast({
            title: "Success",
            description: `Refreshed ${reviewsToInsert.length} reviews for ${outletName}`,
          });
        } else {
          throw new Error(data?.error || "No reviews found");
        }
      } else {
        // Use scrape-outlet edge function for regular outlets
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.error("⏱️ Request timeout after 4 minutes");
          controller.abort();
        }, 240000);

        const result = await supabase.functions.invoke("scrape-outlet", {
          body: { outletId: freshOutlet.id },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        
        data = result.data;
        error = result.error;

        if (error) throw error;

        onProgress?.(100);

        // Update job statuses based on results from scrape-outlet
        // The edge function returns platform-level results
        if (jobId) {
          // Get all jobs for this outlet that were just created
          const { data: jobs } = await supabase
            .from("scraping_jobs")
            .select("id, platform")
            .eq("outlet_id", freshOutlet.id)
            .eq("status", "in_progress");
          
          if (jobs && jobs.length > 0) {
            // Update each job based on the overall result
            // Since scrape-outlet handles multiple platforms, we mark all as completed
            for (const job of jobs) {
              await supabase
                .from("scraping_jobs")
                .update({
                  status: data.failureCount > 0 ? "completed" : "completed",
                  finished_at: new Date().toISOString(),
                  message: data.failureCount > 0 
                    ? `Completed with ${data.failureCount} platform failure(s)`
                    : "Completed successfully"
                })
                .eq("id", job.id);
            }
          }
        }

        if (data.failureCount > 0) {
          toast({
            title: "Partial Success",
            description: `Scraped ${data.successCount}/${data.totalPlatforms} platforms. Failed: ${data.failedPlatforms.join(", ")}`,
          });
        } else {
          toast({
            title: "Success",
            description: `Refreshed reviews from ${data.successCount} platform${data.successCount !== 1 ? 's' : ''} for ${outletName}`,
          });
        }
      }

      onSuccess?.();
      return { success: true, data };
    } catch (error: any) {
      console.error("❌ Error refreshing reviews:", error);
      
      let errorMessage = "Failed to refresh reviews";
      let errorDetails = "";
      
      if (error.name === 'AbortError') {
        errorMessage = "Request timed out after 4 minutes";
        errorDetails = "This might happen if the outlet has many reviews. Try reducing the fetch limit.";
      } else if (error.message?.includes("Outlet not found")) {
        errorMessage = "Outlet configuration error";
        errorDetails = "Please check if the outlet still exists and try again.";
      } else if (error.message?.includes("No platforms")) {
        errorMessage = "No platforms configured";
        errorDetails = "Please add at least one platform URL (Google Maps, Instagram, etc.)";
      } else if (error.message?.includes("disabled")) {
        errorMessage = "Scraping is disabled";
        errorDetails = "Enable scraping for this outlet in settings.";
      } else {
        errorDetails = error.message || "Unknown error occurred. Check console for details.";
      }
      
      // Update all in-progress job records with failure
      if (jobId) {
        const { data: jobs } = await supabase
          .from("scraping_jobs")
          .select("id")
          .eq("outlet_id", outletId)
          .eq("status", "in_progress");
        
        if (jobs && jobs.length > 0) {
          for (const job of jobs) {
            await supabase
              .from("scraping_jobs")
              .update({
                status: "failed",
                finished_at: new Date().toISOString(),
                message: errorDetails,
              })
              .eq("id", job.id);
          }
        }
      }

      toast({
        title: errorMessage,
        description: errorDetails,
        variant: "destructive",
      });

      return { success: false, error };
    } finally {
      setIsRefreshing(false);
    }
  };

  return { refreshOutlet, isRefreshing };
};
